import { useSyncExternalStore } from 'react'
import { redactBody, redactHeaders } from './redact.js'

/** One API call as the inspector shows it. Credentials are redacted before a record is stored. */
export interface ApiCallRecord {
  id: string
  startedAt: string
  durationMs: number
  method: string
  url: string
  requestHeaders: Record<string, string>
  requestBody: string
  /** Null when the request never got a response (network failure, CORS). */
  status: number | null
  responseBody: string
  networkError: string | null
}

const MAX_RECORDS = 50

// Memory only, newest first. Never written to storage: a reload clears it.
let records: readonly ApiCallRecord[] = []
const listeners = new Set<() => void>()

function publish(next: readonly ApiCallRecord[]): void {
  records = next
  for (const listener of listeners) {
    listener()
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function clearApiCalls(): void {
  publish([])
}

export function getApiCalls(): readonly ApiCallRecord[] {
  return records
}

export function useApiCalls(): readonly ApiCallRecord[] {
  return useSyncExternalStore(subscribe, getApiCalls, getApiCalls)
}

function headersAsRecord(headers: HeadersInit | undefined): Record<string, string> {
  return Object.fromEntries(new Headers(headers).entries())
}

function record(entry: ApiCallRecord): void {
  publish([entry, ...records].slice(0, MAX_RECORDS))
}

/**
 * `fetch`, plus a redacted record of the call for the inspector. Behaves
 * exactly like `fetch` for the caller: the same response comes back and the
 * same errors are thrown, and a failure to record never fails the request.
 */
export async function inspectedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const startedAt = new Date()
  const requestHeaders = headersAsRecord(init.headers)
  const base = {
    id: crypto.randomUUID(),
    startedAt: startedAt.toISOString(),
    method: init.method ?? 'GET',
    url,
    requestHeaders: redactHeaders(requestHeaders),
    requestBody: typeof init.body === 'string' ? redactBody(init.body, requestHeaders['content-type'] ?? '') : '',
  }
  const elapsed = (): number => Date.now() - startedAt.getTime()

  let response: Response
  try {
    response = await fetch(url, init)
  } catch (error) {
    record({ ...base, durationMs: elapsed(), status: null, responseBody: '', networkError: error instanceof Error ? error.message : 'Request failed' })
    throw error
  }

  try {
    const text = await response.clone().text()
    record({
      ...base,
      durationMs: elapsed(),
      status: response.status,
      responseBody: redactBody(text, response.headers.get('content-type') ?? ''),
      networkError: null,
    })
  } catch (error) {
    console.error('Could not record an API call for the inspector', error)
  }
  return response
}
